export interface ServiceAvailabilityResponse {
    available: boolean;
    messages: string[];
}

export interface StartBookingResponse {
    session_id: string;
}

export interface ProductGroupItemProduct {
    sku: string;
    name: string;
    manufacturer: {
        key: string;
        name: string;
    };
    color: string;
}

export interface ProductGroupItem {
    id: number;
    key: string;
    name: string;
    color: string;
    product: ProductGroupItemProduct | null;
    group_key: string | null;
}

export interface ProductGroupResponse {
    type: string;
    name: string;
    options: ProductGroupItem[];
}

export type Product = {
    id: number;
    name: string;
    sku: string;
};

export type RepairService = {
    id: number;
    key: string;
    name: string;
};

export interface RepairServiceListResponse {
    services: RepairService[];
}

export type CustomerLineStatus = {
    key: string;
    name: string;
};

export interface CustomerLineOptionsResponse {
    line_statuses: CustomerLineStatus[];
}

export type NewRepairBookingSession = {
    type: 'new';
    csrfToken: string;
    sessionId: string;
    designatedShop: Shop | null;
};

export type ExistingRepairBookingSession = {
    type: 'edit';
    csrfToken: string;
    sessionId: string;
    bookingToken: string;
};

export type RepairBookingSession = NewRepairBookingSession | ExistingRepairBookingSession;

type RepairBooking = {
    shop: Shop | null;
    year: number;
    day: string;
    selectedSlot: AppointmentSlot | null;
    personalInfo: PersonalInfo;
};

export interface NewRepairBooking extends RepairBooking {
    type: 'new';
    product: Product | null;
    repairOptions: RepairService[];
}

export interface ExistingRepairBooking extends RepairBooking {
    type: 'existing';
    confirmationId: string;
    bookingToken: string;
}

export type BookingEntrySource = 'web' | 'console';

export type PersonalInfo = {
    first_name: string;
    last_name: string;
    first_name_pronunciation: string;
    last_name_pronunciation: string;
    birthday?: string;
    phone_number: string;
    line_status?: string;
    subscriber_code?: string;
    alt_phone_number: string;
    email: string;
    notes: string;
    is_warranty_member?: boolean;
};

export const createEmptyBooking = (): NewRepairBooking => ({
    type: 'new',
    product: null,
    repairOptions: [],
    shop: null,
    year: 0,
    day: '',
    selectedSlot: null,
    personalInfo: {
        first_name: '',
        last_name: '',
        first_name_pronunciation: '',
        last_name_pronunciation: '',
        phone_number: '',
        alt_phone_number: '',
        email: '',
        notes: '',
        is_warranty_member: false,
    },
});

export const createEmptyPersonalInfo = (): PersonalInfo => ({
    first_name: '',
    last_name: '',
    first_name_pronunciation: '',
    last_name_pronunciation: '',
    phone_number: '',
    alt_phone_number: '',
    email: '',
    notes: '',
    is_warranty_member: false,
});

export type Shop = {
    shop_id: string;
    name: string;
    country: string;
    postal_code: string;
    region: string;
    address: string;
    phone_number: string;
    latitude: string;
    longitude: string;
    map_url: string;
    notes: string;
};

export interface ShopListResponse {
    shops: Shop[];
}

export type AppointmentSlot = {
    appointment_id: string;
    start_at: string;
};

export type Appointment = {
    date: string;
    slots: AppointmentSlot[];
};

export interface AppointmentListResponse {
    appointments: Appointment[];
}

export interface BookingCreateResponse {
    confirmation_id: string;
}

export interface BookingDetailsResponse {
    confirmation_id: string;
    shop: Shop;
    customer: PersonalInfo;
    slot: AppointmentSlot;
}

export interface BookingRescheduleResponse {
    confirmation_id: string;
}

export type TimeRange = { start: string; end: string };

export type Day = { rawDate: string; date: string; day: string };

export type BookingSearchParams = {
    first_name_pronunciation: string;
    last_name_pronunciation: string;
    phone_number: string;
    confirmation_id: string;
    shop_name: string;
    from_date: string;
    to_date: string;
};

export type BookingSearchResult = {
    first_name_pronunciation: string;
    last_name_pronunciation: string;
    phone_number: string;
    alt_phone_number: string;
    confirmation_id: string;
    shop_name: string;
    booking_token: string;
    start_at: string;
    last_updater: string;
};

export interface BookingSearchResponse {
    bookings: BookingSearchResult[];
}

export type PersonalInfoConfig =
    | { granularity: 'standard' }
    | { granularity: 'extended'; lineStatuses: CustomerLineStatus[] };

export type SessionType = 'new' | 'reschedule';
